import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { DisciplineReferralStudent } from '../../classroom360/discipline-referral/discipline-referral-student.model';
import { DisciplineReferral } from '../../classroom360/discipline-referral/discipline-referral.model';
import { BehaviorEvent } from '../../counselor360/behavior/models/behavior-event.model';
import { BehaviorIncident } from '../../counselor360/behavior/models/behavior-incident.model';
import { BehaviorStudent } from '../../counselor360/behavior/models/behavior-student.model';
import { LoginService } from '../../shared-modules/login/login.service';
import { Lookup } from '../lookup/lookup.model';
import { System } from '../System';
import { UserService } from '../user/user.service';
import { YearService } from '../year/year.service';

@Injectable()
export class BehaviorSharedService {

    locations: Lookup[] = [];
    incidentCodes: Lookup[] = [];
    contextCodes: Lookup[] = [];
    reportedByCodes: Lookup[] = [];
    behaviorEvent: BehaviorEvent = new BehaviorEvent();
    eventCreatedFromReferralPage = false;
    referralCreated = false;

    constructor(
        private http: HttpClient,
        private yearService: YearService,
        private userService: UserService,
        private loginService: LoginService,
        private router: Router
    ) {
    }

    public getDisciplineReferralPlacesLookup(schoolId: number): Observable<Lookup[]> {
        return this.http.get<Lookup[]>(System.apiURL.Counselor + `GetDisciplineReferralPlacesLookup?schoolId=${schoolId}`);
    }

    public getDisciplineReferralLookups(lookupType: string, yearId: number, schoolId: number): Observable<Lookup[]> {
        return this.http.get<Lookup[]>(System.apiURL.Counselor + `getDisciplineReferralLookups?type=${lookupType}&yearId=${yearId}&schoolId=${schoolId}`);
    }

    public disciplineReferralIncidentLookup(yearId: number, profileArea: string): Observable<Lookup[]> {
        return this.http.get<Lookup[]>(System.apiURL.Counselor + `GetDisciplineReferralIncidentLookup?yearId=${yearId}&schoolId=${this.yearService.defaultYear.SchoolID}&profileArea=${profileArea}`);
    }

    getClassroomDisciplineReferrals(showAllStaffEntries: boolean, hideTeams: boolean = false, referralStatusId: number = 0): Observable<DisciplineReferral[]> {
        // PRE: a boolean to show all staff entries or only the current ones, boolean to hide teams referrals when teams is active for the site
        // POST: obtains a list of discipline referrals
        return this.http.get<DisciplineReferral[]>(System.apiURL.Counselor + `GetClassroomDisciplineReferrals?yearId=${this.yearService.defaultYear.Id}&schoolId=${this.yearService.defaultYear.SchoolID}&AYIdentifier=${this.yearService.defaultYear.AYIdentifier}&showAllStaffEntries=${showAllStaffEntries}&hideTeams=${hideTeams}&statusId=${referralStatusId}`);
    }

    saveClassroomDisciplineReferral(referrals: DisciplineReferral[]): Observable<string> {
        // PRE: a list of discipline referrals
        // POST: saves those referrals to the server
        // We need to create copies of the referrals to ensure that the PBIS fields are strings
        const copiedReferrals = [];
        referrals.forEach(referral => {
            const copiedReferral = new DisciplineReferral();
            Object.assign(copiedReferral, referral);
            if (copiedReferral.PBISSelectedOthersInvolved instanceof Array) {
                copiedReferral.PBISSelectedOthersInvolved = copiedReferral.PBISSelectedOthersInvolved.join();
            }
            if (copiedReferral.PBISSelectedPriorInterventions instanceof Array) {
                copiedReferral.PBISSelectedPriorInterventions = copiedReferral.PBISSelectedPriorInterventions.join();
            }
            copiedReferrals.push(copiedReferral);
        });
        // Sending request with copied referrals
        return this.http.post<string>(System.apiURL.Counselor + `SaveClassroomDisciplineReferral?yearId=${this.yearService.defaultYear.Id}&schoolId=${this.yearService.defaultYear.SchoolID}`, copiedReferrals);
    }

    public saveBehaviorEvent(e: BehaviorEvent): Observable<BehaviorEvent> {
        return this.http.post<BehaviorEvent>(System.apiURL.Counselor + 'SaveBehaviorEvent', e);
    }

    public saveBehaviorIncident(incident: BehaviorIncident): Observable<BehaviorIncident> {
        return this.http.post<BehaviorIncident>(System.apiURL.Counselor + 'SaveBehaviorIncident', incident);
    }

    getStudentsFromReferral(behaviorEvent: BehaviorEvent, referral: DisciplineReferral) {
        if (referral && referral.Students.length > 0) {
            behaviorEvent.Incidents = [];
            behaviorEvent.Actions = [];
            referral.Students.forEach(student => {
                const behaviorStudent = new BehaviorStudent();
                if (!student.UnknownStudent && !student.NonStudent) {
                    behaviorStudent.StudentId = student.Id;
                }
                behaviorStudent.MemId = student.MemId;
                behaviorStudent.FirstName = student.FirstName;
                behaviorStudent.MiddleName = student.MiddleName;
                behaviorStudent.LastName = student.LastName;
                behaviorStudent.Nickname = student.NickName;
                behaviorStudent.StudentNumber = student.StudentNumber;
                behaviorStudent.Involvement = this.loginService.LoginSettings.V10 ? DisciplineReferralStudent.InvolvementTypes[student.InvolvementType] : DisciplineReferralStudent.V9InvolvementTypes[2];
                behaviorStudent.Alerts = student.StudentAlerts;
                behaviorStudent.IsUnknownStudent = student.UnknownStudent;
                behaviorStudent.PhoneNumber = student.CellPhone;
                behaviorStudent.Gender = student.Gender;
                behaviorStudent.DOB = moment(student.DateOfBirth).toDate();
                behaviorStudent.IncidentCount = 1;
                behaviorEvent.Students.push(behaviorStudent);

                if (this.loginService.LoginSettings.V10) {
                    const behaviorIncident = new BehaviorIncident();
                    behaviorIncident.EventId = behaviorEvent.Id;
                    if (!student.UnknownStudent && !student.NonStudent) {
                        behaviorIncident.StudentId = student.Id;
                    }
                    behaviorIncident.InvolvementType = DisciplineReferralStudent.InvolvementTypes[student.InvolvementType];
                    behaviorIncident.IsNonStudent = student.NonStudent;
                    behaviorIncident.IsUnknownStudent = student.UnknownStudent;
                    behaviorIncident.Comments = student.IncidentComments;
                    if (behaviorStudent.Involvement == 'Offender') {
                        const code = this.incidentCodes.find(c => c.Id == student.DisciplineCode.toString());
                        if (code != null) {
                            behaviorIncident.IncidentCode = code.Code;
                            if (!behaviorEvent.DefaultIncidentCodeId) {
                                behaviorEvent.DefaultIncidentCodeId = code.Code;
                            }
                        }
                    }
                    behaviorIncident.AddedOn = new Date();
                    behaviorIncident.AddedBy = this.userService.currentUser.MemberId;
                    if (student.InvolvingType == '1') {
                        const selectedCode = this.incidentCodes.find(c => c.Code == behaviorIncident.IncidentCode);
                        if (selectedCode != null) {
                            behaviorIncident.DCID = parseInt(selectedCode.Id, 10);
                        }
                    }
                    this.saveBehaviorIncident(behaviorIncident).subscribe(incident => {
                        Object.assign(behaviorIncident, incident);
                        behaviorStudent.Id = incident.Id;
                        behaviorEvent.Incidents.push(behaviorIncident);
                    });
                } else {
                    if (behaviorStudent.IsUnknownStudent == false && behaviorStudent.StudentId > 0) {
                        const behaviorIncident = new BehaviorIncident();
                        behaviorIncident.StudentId = behaviorStudent.StudentId;
                        behaviorIncident.EventId = behaviorEvent.Id;
                        behaviorIncident.MemId = student.MemId;
                        behaviorIncident.IncidentCode = behaviorEvent.EventName;
                        behaviorIncident.DCID = +behaviorEvent.EventNumber;
                        behaviorIncident.InvolvementType = DisciplineReferralStudent.V9InvolvementTypes[2];
                        behaviorIncident.IncidentsFunctionalCodeId = +behaviorEvent.IncidentsFunctionalCodeId;
                        behaviorIncident.Date = new Date(behaviorEvent.Date);
                        behaviorIncident.IncidentTime = behaviorEvent.IncidentTime.trim();
                        behaviorIncident.SchoolId = behaviorEvent.SchoolId;
                        behaviorIncident.AYIdentifier = behaviorEvent.AYIdentifier.toString();
                        behaviorIncident.Location = behaviorEvent.RoomNumber;
                        behaviorIncident.ReportedBy = behaviorEvent.ReportedBy;
                        behaviorIncident.EnteredBy = behaviorEvent.EnteredBy;
                        behaviorIncident.AddedByStaff = this.userService.currentUser.LastName + ' ' + this.userService.currentUser.FirstName;
                        behaviorIncident.IncidentRefferalId = referral.Id;
                        if (behaviorStudent.Involvement == 'Offender') {
                            const code = this.incidentCodes.find(c => c.Id == student.DisciplineCode.toString());
                            if (code != null) {
                                behaviorIncident.IncidentCode = code.Code;
                                if (!behaviorEvent.DefaultIncidentCodeId) {
                                    behaviorEvent.DefaultIncidentCodeId = code.Code;
                                }
                            }
                        }
                        behaviorIncident.Comments = behaviorEvent.StaffComment;
                        this.saveBehaviorIncident(behaviorIncident).subscribe(incident => {
                            Object.assign(behaviorIncident, incident);
                            behaviorStudent.Id = incident.StudentId;
                            behaviorStudent.StudentId = incident.StudentId;
                            behaviorStudent.UserHasPermissionForStudent = true;
                            behaviorEvent.Incidents.push(behaviorIncident);
                        });
                    }
                }
            });
        }
        referral = null;
    }

    setInitialValues(behaviorEvent: BehaviorEvent) {
        if (behaviorEvent.Context == null || behaviorEvent.Context == '') {
            const onCampusContext = this.contextCodes.filter(function (o) {
                return o.CodeDescription.indexOf('On Campus') > -1;
            });
            if (onCampusContext.length > 0) {
                behaviorEvent.Context = onCampusContext[0].Id;
            } else if (this.contextCodes.length > 0) {
                behaviorEvent.Context = this.contextCodes[0].Id;
            }
        }
        if (behaviorEvent.DefaultIncidentCodeId == null || behaviorEvent.DefaultIncidentCodeId == '') {
            if (this.incidentCodes.length > 0) {
                behaviorEvent.DefaultIncidentCodeId = this.incidentCodes[0].Code;
            }
        }
    }

    acceptReferrals(selectedReferrals: DisciplineReferral[], openEvent: boolean = false) {
        this.loginService.getStaffIdByUserId().subscribe(staffId => {
            selectedReferrals.forEach(referral => {
                const behaviorEvent: BehaviorEvent = new BehaviorEvent();
                behaviorEvent.SchoolId = this.yearService.defaultYear.SchoolID;
                this.setInitialValues(behaviorEvent);

                // Performing conversions
                referral = System.convert<DisciplineReferral>(referral, new DisciplineReferral());
                if (referral.Students) {
                    for (let j = 0; j < referral.Students.length; j++) {
                        referral.Students[j] = System.convert<DisciplineReferralStudent>(referral.Students[j], new DisciplineReferralStudent());
                    }
                }

                behaviorEvent.ReferralId = referral.Id;
                behaviorEvent.Date = moment(referral.Date.toString()).toDate();
                behaviorEvent.Date.setUTCSeconds(referral.Date.getSeconds());
                behaviorEvent.Date.setUTCHours(referral.Date.getHours());
                behaviorEvent.Date.setUTCMinutes(referral.Date.getMinutes());
                behaviorEvent.Location = referral.Location;
                behaviorEvent.StaffComment = referral.EventComment;
                behaviorEvent.EnteredBy = staffId;
                behaviorEvent.Context = referral.Context;
                behaviorEvent.Description = referral.Description;
                behaviorEvent.AddedOn = new Date();
                behaviorEvent.AddedBy = this.userService.currentUser.MemberId;
                behaviorEvent.EventName = 'Referral - ' + referral.StaffName + ' - ' + referral.Location;
                behaviorEvent.AYIdentifier = this.yearService.defaultYear.AYIdentifier;
                if (!this.loginService.LoginSettings.V10) {
                    if (referral.IncidentCodeId == null || referral.IncidentCodeId == '' || referral.IncidentCodeId == '0') {
                        if (this.incidentCodes.length > 0) {
                            behaviorEvent.DefaultIncidentCodeId = this.incidentCodes.find(i => i.Id != '0').Id;
                            behaviorEvent.EventNumber = behaviorEvent.DefaultIncidentCodeId;
                        }
                    } else {
                        behaviorEvent.EventNumber = referral.IncidentCodeId;
                    }
                    behaviorEvent.IncidentTime = referral.Time.trim();
                    behaviorEvent.AYIdentifier = this.yearService.defaultYear.Id.toString();
                    behaviorEvent.AddedBy = this.userService.currentUser.Id;
                    behaviorEvent.LocationId = referral.LocationId;
                    behaviorEvent.StaffComment = referral.Description;
                }
                this.saveBehaviorEvent(behaviorEvent).subscribe(result => {
                    if (result) {
                        Object.assign(behaviorEvent, result);
                        if (behaviorEvent.Id > 0 && referral) {
                            referral.EventId = behaviorEvent.Id;
                            this.getStudentsFromReferral(behaviorEvent, referral);
                            const referralAsList: DisciplineReferral[] = [referral];
                            this.saveClassroomDisciplineReferral(referralAsList).subscribe(message => {
                                this.behaviorEvent = behaviorEvent;
                                if (openEvent) {
                                    this.eventCreatedFromReferralPage = true;
                                    this.router.navigateByUrl('/counselor360/behavior/behaviorEvents');
                                }
                            });
                        }
                    }
                });
            });
        });
    }

    // V9 only
    acceptReferralsinOpenEventModel(selectedReferrals: DisciplineReferral[], openEvent: boolean = false): Observable<boolean> {
        const observableEventCreatedFromReferral = new Subject<boolean>();
        const observableEventCreatedFromReferralStream$ = observableEventCreatedFromReferral.asObservable();

        this.loginService.getStaffIdByUserId().subscribe(staffId => {
            selectedReferrals.forEach(referral => {
                const behaviorEvent: BehaviorEvent = new BehaviorEvent();
                behaviorEvent.SchoolId = this.yearService.defaultYear.SchoolID;
                this.setInitialValues(behaviorEvent);

                // Performing conversions
                referral = System.convert<DisciplineReferral>(referral, new DisciplineReferral());
                if (referral.Students) {
                    for (let j = 0; j < referral.Students.length; j++) {
                        referral.Students[j] = System.convert<DisciplineReferralStudent>(referral.Students[j], new DisciplineReferralStudent());
                    }
                }

                behaviorEvent.ReferralId = referral.Id;
                behaviorEvent.Date = moment(referral.Date.toString()).toDate();
                behaviorEvent.Date.setUTCSeconds(referral.Date.getSeconds());
                behaviorEvent.Date.setUTCHours(referral.Date.getHours());
                behaviorEvent.Date.setUTCMinutes(referral.Date.getMinutes());
                behaviorEvent.Location = referral.Location;
                behaviorEvent.RoomNumber = referral.RoomBus;
                if (referral.FunctionalMotivationId != null && referral.FunctionalMotivationId != '' && referral.FunctionalMotivationId != '0') {
                    behaviorEvent.IncidentsFunctionalCodeId = parseInt(referral.FunctionalMotivationId, 10);
                }
                behaviorEvent.StaffComment = referral.EventComment;

                const reportedBy = Number(referral.ReportedBy);
                if (!isNaN(reportedBy)) {
                    behaviorEvent.ReportedBy = reportedBy;
                }
                behaviorEvent.EnteredBy = staffId;
                behaviorEvent.Context = referral.Context;
                behaviorEvent.Description = referral.Description;
                behaviorEvent.AddedOn = new Date();
                behaviorEvent.AddedBy = this.userService.currentUser.MemberId;
                behaviorEvent.EventName = 'Referral - ' + referral.StaffName + ' - ' + referral.Location;
                behaviorEvent.AYIdentifier = this.yearService.defaultYear.AYIdentifier;
                if (referral.ReferralSiteId > 0) {
                    behaviorEvent.SchoolId = referral.ReferralSiteId;
                }
                if (referral.IncidentCodeId == null || referral.IncidentCodeId == '' || referral.IncidentCodeId == '0') {
                    if (this.incidentCodes.length > 0 && openEvent === false) {
                        behaviorEvent.DefaultIncidentCodeId = this.incidentCodes.find(i => i.Id != '0').Id;
                        behaviorEvent.EventNumber = behaviorEvent.DefaultIncidentCodeId;
                    }
                } else {
                    behaviorEvent.EventNumber = referral.IncidentCodeId;
                }

                behaviorEvent.IncidentTime = referral.Time.trim();
                behaviorEvent.AYIdentifier = this.yearService.defaultYear.Id.toString();
                behaviorEvent.AddedBy = this.userService.currentUser.Id;
                behaviorEvent.LocationId = referral.LocationId;
                behaviorEvent.StaffComment = referral.Description;
                if (!openEvent) {
                    this.saveBehaviorEvent(behaviorEvent).subscribe(result => {
                        if (result) {
                            Object.assign(behaviorEvent, result);
                            if (behaviorEvent.Id > 0 && referral) {
                                referral.EventId = behaviorEvent.Id;
                                this.getStudentsFromReferral(behaviorEvent, referral);
                                const referralAsList: DisciplineReferral[] = [referral];
                                this.behaviorEvent = behaviorEvent;
                                if (this.referralCreated == true) {
                                    this.referralCreated = false;
                                    observableEventCreatedFromReferral.next(true);
                                }
                                else {
                                    this.saveClassroomDisciplineReferral(referralAsList).subscribe(message => {
                                        observableEventCreatedFromReferral.next(true);
                                        if (openEvent) {
                                            this.eventCreatedFromReferralPage = true;
                                            this.router.navigateByUrl('/counselor360/behavior/behaviorEvents');
                                        }
                                    });
                                }
                            }
                        }
                    });
                }
                else {
                    this.behaviorEvent = behaviorEvent;
                    observableEventCreatedFromReferral.next(false);
                }
            });
        });

        return observableEventCreatedFromReferralStream$;
    }

    syncToServer(referral: DisciplineReferral) {
        // POST: syncs the data from the local instance to the server representation
        if (referral.PBISSelectedOthersInvolved && referral.PBISSelectedOthersInvolved instanceof Array) {
            let othersInvolvedString = '';
            for (let i = 0; i < referral.PBISSelectedOthersInvolved.length; i++) {
                if (i > 0) {
                    othersInvolvedString += ',';
                }
                othersInvolvedString += referral.PBISSelectedOthersInvolved[i];
            }
            referral.PBISSelectedOthersInvolved = othersInvolvedString;
        }
        if (referral.PBISSelectedPriorInterventions && referral.PBISSelectedPriorInterventions instanceof Array) {
            let priorInterventionsString = '';
            for (let i = 0; i < referral.PBISSelectedPriorInterventions.length; i++) {
                if (i > 0) {
                    priorInterventionsString += ',';
                }
                priorInterventionsString += referral.PBISSelectedPriorInterventions[i];
            }
            referral.PBISSelectedPriorInterventions = priorInterventionsString;
        }
    }
}
